/**
 * Query hooks for Clubs
 * 
 * These hooks encapsulate all club-related data fetching
 * with automatic caching, deduplication, and refetching.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query/client';
import { useAuth } from '@/contexts/AuthContext';
import { BACKEND_URL } from "../../config";
/**
 * Fetch public clubs (for ClubsPage)
 */
export function usePublicClubs() {
  return useQuery({
    queryKey: queryKeys.clubs.public(),
    queryFn: async () => {
      const res = await api.get("/api/clubs/public");
      return res.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch most active clubs (for ClubsPage)
 */
export function useMostActiveClubs(limit = 100) {
  return useQuery({
    queryKey: [...queryKeys.clubs.all(), 'most-active', limit],
    queryFn: async () => {
      const res = await api.get(`/api/clubs/most-active?limit=${limit}`);
      return res.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Fetch a single club by ID
 * Supports both authenticated and public endpoints with automatic fallback
 */
export function useClub(clubId, { isAuthenticated = false, ...options } = {}) {
  return useQuery({
    queryKey: queryKeys.clubs.detail(clubId),
    queryFn: async () => {
      if (isAuthenticated) {
        try {
          const res = await api.get(`/api/clubs/${clubId}`);
          return { ...res.data, access_denied: false };
        } catch (authError) {
          // If 403 or 404, try public endpoint
          if (authError.response?.status === 403 || authError.response?.status === 404) {
            const res = await api.get(`/api/clube/${clubId}/public`);
            return res.data;
          }
          throw authError;
        }
      } else {
        // Not authenticated - use public endpoint
        const res = await api.get(`/api/clube/${clubId}/public`);
        return res.data;
      }
    },
    enabled: !!clubId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Fetch club gallery
 */
export function useClubGallery(clubId, { enabled = true } = {}) {
  return useQuery({
    queryKey: [...queryKeys.clubs.detail(clubId), 'gallery'],
    queryFn: async () => {
      const res = await api.get(`/api/clubs/${clubId}/gallery`);
      return res.data.gallery || [];
    },
    enabled: !!clubId && enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch club videos
 */
export function useClubVideos(clubId, { enabled = true } = {}) {
  return useQuery({
    queryKey: [...queryKeys.clubs.detail(clubId), 'videos'],
    queryFn: async () => {
      const res = await api.get(`/api/clubs/${clubId}/videos`);
      return res.data?.videos || res.data || [];
    },
    enabled: !!clubId && enabled,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch club members
 */
export function useClubMembers(clubId) {
  return useQuery({
    queryKey: queryKeys.clubs.members(clubId),
    queryFn: async () => {
      const res = await api.get(`/api/clubs/${clubId}/members`);
      return res.data;
    },
    enabled: !!clubId,
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch regional maps settings
 */
export function useRegionalMapsSettings() {
  return useQuery({
    queryKey: ['settings', 'regional-maps'],
    queryFn: async () => {
      const res = await api.get("/api/settings/regional-maps");
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}


/**
 * Fetch user's own clubs (memberships)
 */
export function useMyClubs() {
  return useQuery({
    queryKey: ['my-clubs'],
    queryFn: async () => {
      const res = await api.get("/api/my-clubs");
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch clubs where user is moderator (for trip migration)
 */
export function useModeratorClubs() {
  return useQuery({
    queryKey: ['my-clubs', 'moderator'],
    queryFn: async () => {
      const res = await api.get("/api/my-clubs");
      return res.data.filter(
        club => (club.role === "moderator" || club.role === "owner" || club.is_moderator) && club.data_ok
      );
    },
    staleTime: 60 * 1000,
  });
}


/**
 * Fetch all my clubs data for MyClubsEdit page
 * Returns moderator clubs, member clubs, pending requests, and invitations
 */
export function useMyClubsData() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-clubs', 'all-data'],
    enabled: !!user,  // only fetch club data for authenticated users (avoids 401 noise when logged out)
    queryFn: async () => {
      const [clubsRes, myPendingRes, invitationsRes] = await Promise.all([
        api.get("/api/my-clubs"),
        // MY pending requests = requests I made to join OTHER clubs
        api.get("/api/my-clubs/my-pending-requests").catch(() => ({ data: [] })),
        api.get("/api/my-clubs/invitations").catch(() => ({ data: [] }))
      ]);
      
      const clubs = clubsRes.data || [];
      
      // Unified list sorted by role: owner > moderator > member
      const roleOrder = { owner: 0, moderator: 1, admin: 1, member: 2 };
      const allClubs = [...clubs].sort((a, b) => {
        const aRole = a.is_creator ? "owner" : a.role;
        const bRole = b.is_creator ? "owner" : b.role;
        return (roleOrder[aRole] ?? 3) - (roleOrder[bRole] ?? 3);
      });
      
      // Legacy splits (kept for backward compat with other consumers)
      const moderatorClubs = clubs.filter(
        club => club.role === "owner" || club.role === "moderator" || club.is_moderator
      );
      const memberClubs = clubs.filter(
        club => club.role === "member" && !club.is_moderator
      );
      
      return {
        allClubs,
        moderatorClubs,
        memberClubs,
        pendingRequests: myPendingRes.data || [],
        invitations: invitationsRes.data || []
      };
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch incoming requests for my clubs (people wanting to join my clubs)
 * Uses the same endpoint as pending-requests which returns requests TO my clubs
 */
export function useIncomingClubRequests() {
  return useQuery({
    queryKey: ['my-clubs', 'incoming-requests'],
    queryFn: async () => {
      const res = await api.get("/api/my-clubs/pending-requests");
      return res.data || [];
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Fetch club settings (creation enabled, etc.)
 */
export function useClubSettings() {
  return useQuery({
    queryKey: ['settings', 'clubs'],
    queryFn: async () => {
      const res = await api.get("/api/settings/clubs");
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}


/**
 * Search clubs by location and name
 * This is a lazy query - only fetches when explicitly called
 */
export function useClubSearch() {
  const [searchParams, setSearchParams] = useState(null);
  
  const query = useQuery({
    queryKey: ['clubs', 'search', searchParams],
    queryFn: async () => {
      if (!searchParams) return [];
      
      const params = new URLSearchParams();
      params.append("lat", searchParams.lat);
      params.append("lng", searchParams.lng);
      params.append("radius_km", searchParams.radius);
      params.append("limit", 20);
      if (searchParams.searchName) {
        params.append("search", searchParams.searchName);
      }
      
      const res = await api.get(`/api/clubs?${params.toString()}`);
      return res.data.slice(0, 20);
    },
    enabled: !!searchParams,
    staleTime: 30 * 1000,
  });
  
  const search = (params) => {
    setSearchParams(params);
  };
  
  const reset = () => {
    setSearchParams(null);
  };
  
  return {
    ...query,
    search,
    reset,
    searchParams
  };
}

